/**
 * remark plugin: turn stock cashtags ($NVDA, $AMD, $^GSPC, $BRK.B) inside prose
 * into links to Yahoo Finance. Runs on the mdast, so it naturally skips code
 * spans/blocks (those are `inlineCode`/`code` nodes, not `text`) and never nests
 * inside existing links.
 */
// Matches an explicit cashtag ($NVDA, $^GSPC, $BRK.B, even $HTZ.US) OR a bare exchange-
// suffixed ticker the model sometimes writes despite the cashtag rule (HTZ.US, RGC.US).
const CASHTAG = /\$(\^?[A-Za-z][A-Za-z0-9.\-]{0,9})|\b([A-Z]{1,5})\.(?:US|NYSE|NASDAQ)\b/g

export function yahooQuoteUrl(ticker: string): string {
  return `https://finance.yahoo.com/quote/${encodeURIComponent(ticker)}`
}

// The clean ticker for the link/label: strip a trailing exchange suffix (Yahoo uses the
// bare US symbol). m[1] = cashtag capture, m[2] = bare TICKER.EXCHANGE capture.
function tickerFromMatch(m: RegExpExecArray): string {
  return (m[1] ?? m[2] ?? '').replace(/\.(US|NYSE|NASDAQ|AS|XETRA|L|TO|HK)$/i, '')
}

type MdNode = {
  type: string
  value?: string
  url?: string
  title?: string
  children?: MdNode[]
}

function splitCashtags(value: string): MdNode[] {
  CASHTAG.lastIndex = 0
  if (!CASHTAG.test(value)) return [{ type: 'text', value }]
  CASHTAG.lastIndex = 0
  const nodes: MdNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  while ((m = CASHTAG.exec(value)) !== null) {
    if (m.index > last) nodes.push({ type: 'text', value: value.slice(last, m.index) })
    const ticker = tickerFromMatch(m)
    nodes.push({
      type: 'link',
      url: yahooQuoteUrl(ticker),
      title: `${ticker} on Yahoo Finance`,
      children: [{ type: 'text', value: `$${ticker}` }],
    })
    last = m.index + m[0].length
  }
  if (last < value.length) nodes.push({ type: 'text', value: value.slice(last) })
  return nodes
}

function walk(node: MdNode): void {
  if (!Array.isArray(node.children)) return
  const out: MdNode[] = []
  for (const child of node.children) {
    if (child.type === 'text') {
      out.push(...splitCashtags(child.value ?? ''))
    } else {
      // recurse into containers, but never into code or existing links
      if (child.type !== 'inlineCode' && child.type !== 'code' && child.type !== 'link') {
        walk(child)
      }
      out.push(child)
    }
  }
  node.children = out
}

export function remarkCashtags() {
  return (tree: MdNode) => walk(tree)
}
